import { useState, type ReactNode } from 'react'
import {
  Menu, PanelLeftClose, PanelLeft, Moon, Sun, ChevronDown,
  HelpCircle, Shield, LogOut,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { FinSidebar } from '@/components/sidebar/FinSidebar'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useAuth } from '@/context/AuthContext'

interface AppShellProps {
  /** i18n translate function */
  t: (key: string) => string
  /** Current theme */
  theme: 'light' | 'dark'
  /** Toggle theme callback */
  toggleTheme: () => void
  /** Routed page content */
  children: ReactNode
  /** Callback when admin panel is requested */
  onShowAdmin: () => void
  /** Callback when FAQ is requested */
  onShowFaq: () => void
}

export function AppShell({ t, theme, toggleTheme, children, onShowAdmin, onShowFaq }: AppShellProps) {
  const { user, logout, isAdmin } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  if (!user) return null

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      {/* ═══ TOP BAR ═══ */}
      <div className="flex items-center justify-between px-4 py-2 border-b shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-7 w-7 md:hidden" onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
            <Menu className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 hidden md:flex" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeft className="h-3.5 w-3.5" />}
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <img src="/logo.svg" alt="Logo" className="h-5 logo-themed" />
          <Separator orientation="vertical" className="h-5" />
          <span className="text-sm font-semibold tracking-tight">{t("app.title")}</span>
          <Badge variant="outline" className="text-[10px] text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">{t("app.connected")}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggleTheme}>
            {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-semibold">
                  {user.name[0]}
                </div>
                <span className="text-xs">{user.name}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-medium">{user.name}</div>
                <div className="text-xs text-muted-foreground">{user.email}</div>
                <div className="flex gap-1 mt-1.5">
                  <Badge variant={user.role === "admin" ? "destructive" : "default"} className="text-[10px]">{user.role}</Badge>
                  <Badge variant="outline" className="text-[10px]">{user.permissions.access}</Badge>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onShowFaq}><HelpCircle className="h-4 w-4" />{t("user.faq")}</DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={onShowAdmin}><Shield className="h-4 w-4" />{t("user.admin")}</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout}><LogOut className="h-4 w-4" />{t("user.signOut")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ═══ MAIN BODY: SIDEBAR + CONTENT ═══ */}
      <div className="flex-1 flex overflow-hidden relative">
        {mobileSidebarOpen && (
          <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setMobileSidebarOpen(false)} />
        )}

        {/* ═══ LEFT SIDEBAR ═══ */}
        <div
          className={[
            "flex-shrink-0 flex flex-col overflow-hidden transition-all duration-200 border-r bg-background",
            "fixed z-50 h-[calc(100vh-49px)] w-[220px]",
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full",
            "md:relative md:z-auto md:h-auto md:translate-x-0",
            sidebarOpen ? "md:w-[220px]" : "md:w-0 md:border-r-0",
          ].join(" ")}
        >
          <FinSidebar onMobileClose={() => setMobileSidebarOpen(false)} />
        </div>

        {/* ═══ RIGHT CONTENT AREA ═══ */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <ErrorBoundary fallbackMessage="Something went wrong loading this page">
            <div className="flex-1 overflow-auto">
              {children}
            </div>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}
